import React, { useState, useEffect } from 'react';
import { NAV_ITEMS } from '../constants';
import { Menu, X, Facebook, Instagram, Star } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

interface HeaderProps {
  onNavigate: (view: string) => void;
  currentView: string;
}

const Header: React.FC<HeaderProps> = ({ onNavigate, currentView }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith('#')) {
      e.preventDefault();
      onNavigate(href);
      setIsOpen(false);
      window.scrollTo(0, 0);
    } else {
      if (currentView !== 'home') {
        e.preventDefault();
        onNavigate('home');
      }
      setIsOpen(false);
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onNavigate('home');
    window.scrollTo(0, 0);
  };

  const handleSocialClick = (platform: string) => {
    trackEvent('click_social', {
      event_category: 'social',
      event_label: platform
    });
  };

  const handleReviewClick = (platform: string) => {
    trackEvent('click_review_link', {
      event_category: 'social',
      event_label: platform
    });
  };

  const isSolid = isScrolled || currentView !== 'home';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex flex-col">
      {/* Top Bar - Socials & Reviews */}
      <div className="bg-slate-900 text-stone-300 py-1.5 border-b border-white/10 relative z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-1.5 text-xs">
          
          {/* Social Links */}
          <div className="flex items-center gap-4">
             <span className="font-bold text-slate-400 uppercase tracking-wider hidden sm:inline">Follow Us:</span>
             <div className="flex gap-1">
                <a href="https://www.facebook.com/BordensBookshoppe" onClick={() => handleSocialClick('facebook')} target="_blank" rel="noopener noreferrer" className="p-2 hover:text-brand-primary transition-colors flex items-center gap-1" aria-label="Follow us on Facebook">
                  <Facebook className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Facebook</span>
                </a>
                <a href="https://www.instagram.com/bordensbookshoppe" onClick={() => handleSocialClick('instagram')} target="_blank" rel="noopener noreferrer" className="p-2 hover:text-brand-primary transition-colors flex items-center gap-1" aria-label="Follow us on Instagram">
                  <Instagram className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Instagram</span>
                </a>
                <a href="https://www.tiktok.com/@bordensbookshoppe" onClick={() => handleSocialClick('tiktok')} target="_blank" rel="noopener noreferrer" className="p-2 hover:text-brand-primary transition-colors flex items-center gap-1" aria-label="Follow us on TikTok">
                  <TikTokIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">TikTok</span>
                </a>
             </div>
          </div>

          {/* Review Links */}
          <div className="flex items-center gap-4">
             <span className="font-bold text-slate-400 uppercase tracking-wider hidden sm:inline">Leave a Review:</span>
             <div className="flex gap-1 items-center">
               <a href="https://g.page/r/CQkHh_dIiBRAEBM/review" onClick={() => handleReviewClick('google')} target="_blank" rel="noopener noreferrer" className="p-2 hover:text-white text-brand-primary font-bold flex items-center gap-1 transition-colors" aria-label="Review us on Google">
                  <Star className="w-3.5 h-3.5 fill-brand-primary" /> Google
               </a>
               <span className="text-slate-600 select-none" aria-hidden="true">|</span>
               <a href="https://www.yelp.com/writeareview/biz/Wuvl8x-e_gcOpcY2VXTUrw?review_origin=biz-page-question-based-entrypoint&toast_recommend=yes" onClick={() => handleReviewClick('yelp')} target="_blank" rel="noopener noreferrer" className="p-2 hover:text-white text-red-400 font-bold flex items-center gap-1 transition-colors" aria-label="Review us on Yelp">
                  <Star className="w-3.5 h-3.5 fill-red-400" /> Yelp
               </a>
             </div>
          </div>

        </div>
      </div>

      {/* Main Navigation */}
      <div className={`transition-all duration-300 w-full ${isSolid ? 'bg-white shadow-md py-1.5' : 'bg-transparent py-3'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            
            <a href="/" onClick={handleLogoClick} className="logo-container group" aria-label="Borden's Bookshoppe Home">
              <span className={`font-logo text-xs md:text-sm tracking-[0.15em] transition-colors duration-300 uppercase ${isSolid ? 'text-brand-primary' : 'text-brand-primary'}`}>
                Borden's
              </span>
              <span className={`font-logo text-xl md:text-4xl tracking-tight -mt-2 transition-colors duration-300 ${isSolid ? 'text-brand-dark' : 'text-white'}`}>
                Bookshoppe
              </span>
            </a>

            <nav className="hidden md:flex space-x-6 items-center" aria-label="Main Navigation">
              {NAV_ITEMS.map((item) => (
                <a 
                  key={item.label} 
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`text-sm font-bold uppercase tracking-widest hover:text-brand-primary transition-colors px-3 py-2 rounded-md ${isSolid ? 'text-slate-600' : 'text-gray-100'}`}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            <button 
              className="md:hidden p-3 rounded-md focus:outline-none min-w-[48px] min-h-[48px] flex items-center justify-center"
              onClick={() => setIsOpen(!isOpen)}
              aria-expanded={isOpen}
              aria-label="Toggle mobile menu"
            >
              {isOpen ? (
                <X className={`w-7 h-7 ${isSolid ? 'text-slate-800' : 'text-white'}`} />
              ) : (
                <Menu className={`w-7 h-7 ${isSolid ? 'text-slate-800' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 absolute w-full shadow-2xl left-0 top-full">
            <nav className="px-2 pt-2 pb-6 space-y-1" aria-label="Mobile Navigation">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="block px-4 py-4 rounded-md text-base font-bold text-slate-700 hover:text-brand-primary hover:bg-slate-50 border-b border-stone-50"
                  onClick={(e) => handleNavClick(e, item.href)}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;